import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPublicMenus } from '../utils/api';
import { FaPlus, FaMinus, FaShoppingCart, FaSearch } from 'react-icons/fa';
import { motion } from 'framer-motion';

// Helper to get full image URL (copied from Cart.js)
const BASE_URL = process.env.REACT_APP_API_BASE_URL_IMG;
function getFullImageUrl(imageUrl) {
  if (!imageUrl) return '';
  if (imageUrl.startsWith('http')) return imageUrl;
  const cleanUrl = imageUrl.replace(/^\/?.*uploads\//, '');
  return `${BASE_URL}/uploads/${cleanUrl}`;
}

export default function CustomerRoom() {
  const { restaurantId, roomId } = useParams();
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cart, setCart] = useState([]);
  const [roomNumber, setRoomNumber] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [validationMsg, setValidationMsg] = useState('');
  const [missingFields, setMissingFields] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMenus();
    // Load cart and room info from localStorage if present
    const stored = localStorage.getItem(`room_cart_${restaurantId}_${roomId}`);
    const info = localStorage.getItem(`room_info_${restaurantId}_${roomId}`);
    setCart(stored ? JSON.parse(stored) : []);
    if (info) {
      const { roomNumber: rn, phoneNumber: pn } = JSON.parse(info);
      setRoomNumber(rn || '');
      setPhoneNumber(pn || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, roomId]);

  useEffect(() => {
    localStorage.setItem(`room_cart_${restaurantId}_${roomId}`, JSON.stringify(cart));
  }, [cart, restaurantId, roomId]);

  useEffect(() => {
    localStorage.setItem(`room_info_${restaurantId}_${roomId}`, JSON.stringify({ roomNumber, phoneNumber }));
  }, [roomNumber, phoneNumber, restaurantId, roomId]);

  async function fetchMenus() {
    setLoading(true);
    setError('');
    try {
      const data = await getPublicMenus(restaurantId);
      setMenus(data || []);
      if (data && data.length > 0) {
        setSelectedMenuId((prev) => prev || data[0].id);
      }
    } catch (err) {
      setError('Failed to load menu');
    }
    setLoading(false);
  }

  function addToCart(item) {
    setCart(prev => {
      const found = prev.find(i => i.id === item.id);
      return found
        ? prev.map(i => i.id === item.id ? { ...i, qty: i.qty + 1 } : i)
        : [...prev, { ...item, qty: 1 }];
    });
  }

  function incrementQty(itemId) {
    setCart(prev => prev.map(i => i.id === itemId ? { ...i, qty: i.qty + 1 } : i));
  }

  function decrementQty(itemId) {
    setCart(prev => {
      const found = prev.find(i => i.id === itemId);
      if (!found) return prev;
      if (found.qty === 1) return prev.filter(i => i.id !== itemId);
      return prev.map(i => i.id === itemId ? { ...i, qty: i.qty - 1 } : i);
    });
  }

  function handleViewCart() {
    // client side validation: ensure cart not empty and room/phone provided
    if (!cart || cart.length === 0) {
      setValidationMsg('Your cart is empty.');
      setMissingFields([]);
      return;
    }
    const missing = [];
    if (!roomNumber) missing.push('room');
    if (!phoneNumber) missing.push('phone');
    if (missing.length > 0) {
      setMissingFields(missing);
      setValidationMsg(`Please enter ${missing.map(m => m === 'room' ? 'room number' : 'phone number').join(' and ')}.`);
      return;
    }
    // all good -> clear any validation and navigate
    setValidationMsg('');
    setMissingFields([]);
    localStorage.setItem(`room_cart_${restaurantId}_${roomId}`, JSON.stringify(cart));
    localStorage.setItem(`room_info_${restaurantId}_${roomId}`, JSON.stringify({ roomNumber, phoneNumber }));
    navigate(`/cart?restaurantId=${restaurantId}&roomId=${roomId}`);
  }

  // derive filtered items and totals
  const lowerSearch = searchTerm.trim().toLowerCase();
  let filteredItems = [];
  if (!lowerSearch) {
    const sel = menus.find(m => m.id === selectedMenuId);
    filteredItems = (sel?.items || []).filter(i => i.active !== false).sort((a, b) => a.name.localeCompare(b.name));
  } else {
    filteredItems = menus
      .flatMap(m => (m.items || []).filter(i => i.active !== false))
      .filter(i => i.name.toLowerCase().includes(lowerSearch))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  const totalItems = cart.reduce((s, it) => s + it.qty, 0);
  const subtotal = Math.round(cart.reduce((s, it) => s + Number(it.price || 0) * it.qty, 0) * 100) / 100;
  const tax = Math.round(subtotal * 0.05 * 100) / 100;
  const grandTotal = Math.round((subtotal + tax) * 100) / 100;

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center p-8"
      >
        <div className="inline-block relative w-16 h-16">
          <motion.div
            className="absolute inset-0 border-4 border-rose-400 border-t-transparent rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
        </div>
        <p className="mt-4 text-rose-800 font-medium">Loading menu...</p>
      </motion.div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white p-6 rounded-xl shadow-lg max-w-md mx-4 text-center"
      >
        <div className="text-red-500 text-4xl mb-4">⚠️</div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">Oops!</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={fetchMenus}
          className="px-4 py-2 bg-gradient-to-r from-rose-400 to-amber-400 text-white rounded-lg font-medium hover:shadow-md transition-all"
        >
          Try Again
        </button>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 p-2 md:p-8 flex flex-col">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl w-full mx-auto bg-white rounded-2xl shadow-lg overflow-hidden p-2 md:p-6 flex-1 flex flex-col"
      >
        <div className="relative mb-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-rose-500 to-amber-500">
            Room Service Menu
          </h2>
        </div>

        {/* Room & Phone inputs + Search */}
        <div className="px-4 space-y-3">
          <div className="flex flex-col md:flex-row gap-3">
            <input
              type="text"
              className={`flex-1 px-3 py-2 border rounded-lg text-sm ${missingFields.includes('room') ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Room Number"
              value={roomNumber}
              onChange={e => setRoomNumber(e.target.value)}
            />
            <input
              type="tel"
              className={`flex-1 px-3 py-2 border rounded-lg text-sm ${missingFields.includes('phone') ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Phone Number"
              value={phoneNumber}
              onChange={e => setPhoneNumber(e.target.value)}
            />
          </div>

          <div className="relative">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search items..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Validation shown next to inputs so it doesn't overlap the View Cart button */}
          {validationMsg && missingFields.length > 0 && (
            <div className="text-sm text-red-500 mt-1">
              {validationMsg}
            </div>
          )}
        </div>

        {/* Categories horizontal */}
        {menus.length > 0 && (
          <div className="px-4 py-3">
            <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
              {menus.map(menu => (
                <button
                  key={menu.id}
                  onClick={() => { setSelectedMenuId(menu.id); setSearchTerm(''); }}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                    selectedMenuId === menu.id ? 'bg-rose-500 text-white' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {menu.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Items grid */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {filteredItems.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              {menus.length === 0
                ? 'No menu categories available'
                : searchTerm
                ? `No items found for "${searchTerm}"`
                : 'No items in this category'}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {filteredItems.map(item => {
                const cartItem = cart.find(c => c.id === item.id);
                return (
                  <div key={item.id} className="bg-white rounded-lg p-2 border border-gray-200 flex flex-col">
                    {item.imageUrl && (
                      <img
                        src={getFullImageUrl(item.imageUrl)}
                        alt={item.name}
                        className="w-full h-20 object-cover rounded mb-2"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <div className="text-xs font-semibold text-center mb-1 truncate">{item.name}</div>
                    <div className="text-rose-600 font-bold text-center mb-2">₹{item.price || 0}</div>

                    <div className="mt-auto">
                      {cartItem ? (
                        <div className="flex items-center justify-between">
                          <button
                            onClick={() => decrementQty(item.id)}
                            className="w-8 h-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center"
                          >
                            <FaMinus size={12} />
                          </button>
                          <span className="font-semibold">{cartItem.qty}</span>
                          <button
                            onClick={() => incrementQty(item.id)}
                            className="w-8 h-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center"
                          >
                            <FaPlus size={12} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => addToCart(item)}
                          className="w-full py-2 bg-gradient-to-r from-rose-500 to-amber-500 text-white rounded-lg text-sm font-semibold"
                        >
                          Add
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>

      {/* View Cart Button placed after all categories, responsive */}
      <div className="w-full flex justify-center mt-6 mb-8 md:mb-0 px-4">
        <motion.button
          className={`btn w-full max-w-xs flex items-center justify-between gap-2 text-lg py-3 shadow-lg rounded-full relative overflow-hidden px-4 ${
            cart.length === 0 || !roomNumber || !phoneNumber
              ? 'bg-gray-300 text-gray-500'
              : 'bg-gradient-to-r from-rose-500 to-amber-500 text-white hover:shadow-xl'
          }`}
          onClick={handleViewCart}
          aria-disabled={cart.length === 0 || !roomNumber || !phoneNumber}
          style={{ minHeight: 48 }}
          whileHover={cart.length > 0 && roomNumber && phoneNumber ? { scale: 1.02 } : {}}
          whileTap={cart.length > 0 && roomNumber && phoneNumber ? { scale: 0.98 } : {}}
        >
          <div className="flex items-center gap-3">
            <FaShoppingCart />
            <span>View Cart</span>
          </div>

          <div className="text-right">
            <div className="text-sm font-medium">{totalItems} item{totalItems !== 1 ? 's' : ''}</div>
            <div className="text-sm">₹{grandTotal.toFixed(2)}</div>
          </div>

          {cart.length > 0 && (
            <motion.span
              className="ml-2 bg-white text-rose-600 rounded-full px-2 py-0.5 text-xs font-bold border border-rose-600 absolute -top-2 -right-2"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              key={totalItems}
            >
              {totalItems}
            </motion.span>
          )}
        </motion.button>
      </div>

      <motion.div
        className="text-center text-xs text-gray-400 mt-8 mb-16 md:mb-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        Powered by <span className="font-bold text-rose-400">EasiDine</span>
      </motion.div>
    </div>
  );
}